// userService.js
// Service for user-related API calls: authentication, profile, settings, API keys, password reset

import { POST, BASE_URL } from './httpClient';

/**
 * User authentication and account management functions
 */

// Function to handle user login
export const login = async (email, password) => {
    try {
        const response = await POST(`${BASE_URL}/user/login`, {
            email,
            password,
        });
        return response; // Return the entire response object
    } catch (error) {
        console.error('Error during login:', error);
        throw error; // Rethrow the error for further handling
    }
};

// Function to handle user registration
export const register = async (email, password) => {
    try {
        const response = await POST(`${BASE_URL}/user/register`, {
            email,
            password,
        });
        return response; // Return the entire response object
    } catch (error) {
        console.error('Error during registration:', error);
        throw error; // Rethrow the error for further handling
    }
};

// Function to join the waitlist
export const joinWaitlist = async (email) => {
    try {
        const response = await POST(`${BASE_URL}/user/waitlist`, {
            email,
        });
        return response; // Return the entire response object
    } catch (error) {
        console.error('Error joining waitlist:', error);
        throw error; // Rethrow the error for further handling
    }
};

// Function to authenticate user with session token
export const authenticateUser = async (sessionId) => {
    try {
        const response = await POST(`${BASE_URL}/user/authenticate`, {
            sessionToken: sessionId,
        });
        return response; // Return the entire response object
    } catch (error) {
        console.error('Error authenticating user:', error);
        throw error; // Rethrow the error for further handling
    }
};

// Function to logout user
export const logout = async () => {
    try {
        const response = await POST(`${BASE_URL}/user/logout`);
        return response; // Return the entire response object
    } catch (error) {
        console.error('Error logging out:', error);
        throw error; // Rethrow the error for further handling
    }
};

/**
 * User profile management functions
 */

// Function to get user profile
export const getUserProfile = async () => {
    try {
        const response = await POST(`${BASE_URL}/user/profile`);
        return response;
    } catch (error) {
        console.error('Error fetching user profile:', error);
        throw error;
    }
};

// Function to update user profile
export const updateUserProfile = async (profileData) => {
    try {
        const response = await POST(`${BASE_URL}/user/profile/update`, {
            username: profileData?.username,
            email: profileData?.email
        });
        return response;
    } catch (error) {
        console.error('Error updating user profile:', error);
        throw error;
    }
};

// Function to update user tier
export const updateUserTier = async (tierData) => {
    try {
        const response = await POST(`${BASE_URL}/user/tier/update`, tierData);
        return response;
    } catch (error) {
        console.error('Error updating user tier:', error);
        throw error;
    }
};

/**
 * User settings management functions
 */

// Function to get user settings
export const getUserSettings = async (userId) => {
    try {
        const response = await POST(`${BASE_URL}/user/settings`, {
            userId
        });
        return response;
    } catch (error) {
        console.error('Error fetching user settings:', error);
        throw error;
    }
};

// Function to update user settings
export const updateUserSettings = async (userId, settings) => {
    try {
        const response = await POST(`${BASE_URL}/user/settings/update`, {
            userId,
            userSettings: settings
        });
        return response;
    } catch (error) {
        console.error('Error updating user settings:', error);
        throw error;
    }
};

/**
 * API Key management functions
 */

// Function to fetch the user's API key information (exists, key value, and credits)
export const getApiKeyInfo = async (userId) => {
    try {
        const response = await POST(`${BASE_URL}/user/api-key/info`, {
            userId,
        });
        return response;
    } catch (error) {
        console.error('Error fetching API key info:', error);
        throw error;
    }
};

// Function to request/create a new API key for the user
export const requestApiKey = async (userId) => {
    try {
        const response = await POST(`${BASE_URL}/user/keys/create`, {
            userId,
        });
        return response;
    } catch (error) {
        console.error('Error requesting API key:', error);
        throw error;
    }
};

// Function to delete/revoke the user's API key
export const deleteApiKey = async (userId) => {
    try {
        const response = await POST(`${BASE_URL}/user/keys/delete`, {
            userId,
        });
        return response;
    } catch (error) {
        console.error('Error deleting API key:', error);
        throw error;
    }
};

/**
 * Password reset functions
 */

// Function to change the password for an authenticated user
export const changePassword = async (currentPassword, newPassword) => {
    try {
        const response = await POST(`${BASE_URL}/user/password/update`, {
            currentPassword,
            newPassword,
        });
        return response;
    } catch (error) {
        console.error('Error updating password:', error);
        throw error;
    }
};

// Function to request password reset
export const requestPasswordReset = async (email) => {
    try {
        const response = await POST(`${BASE_URL}/user/password/reset-request`, {
            email,
        });
        return response;
    } catch (error) {
        console.error('Error requesting password reset:', error);
        throw error;
    }
};

// Function to reset password with token
export const resetPassword = async (token, newPassword) => {
    try {
        const response = await POST(`${BASE_URL}/user/password/reset`, {
            token,
            newPassword,
        });
        return response;
    } catch (error) {
        console.error('Error resetting password:', error);
        throw error;
    }
};
